import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { HoverHintContent, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useAuth } from "@/components/AuthProvider";
import { useIntegratorMe } from "@/lib/me-query";
import { hubModuleHasAnyAccess } from "@/lib/subapp-access";
import { cn } from "@/lib/utils";

import { fetchLaborActiveSession } from "@/lib/labor/laborApi";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import { PrePackCartonOperatorStack } from "@/pages/prepack/PrePackCartonOperatorStack";

import {
  CLAMPQC_LABOR_OPEN_FLASH_STORAGE_KEY,
  clampQcInspectionListShouldDisableOpenJobForSlug,
  formatLaborLocationLabel,
} from "./clampQcLaborApi";
import {
  clampQcActiveJobOperatorsQueryKey,
  CLAMP_QC_JOB_PRESENCE_INTERVAL_MS,
  fetchClampQcActiveJobOperators,
  filterClampQcOperatorsForJobSlug,
} from "./clampQcLaborPresenceApi";

import {
  CLAMPQC_HUB_WORK_ORDERS_QUERY_KEY,
  clampQcWorkOrderStatusHoverHintText,
  clampQcWorkOrderStatusChipClassName,
  clampQcWorkOrderStatusChipLabel,
  fetchClampQcHubWorkOrders,
  type ClampQcHubWorkOrderListItem,
} from "./clampQcBuildOrdersApi";
import { type ClampQcWorkOrderBundle } from "./clampQcMockData";

type JobStatusFilter = "all" | ClampQcWorkOrderBundle["status"];

const INSPECTION_LIST_LABOR_PAGE = "clamp-qc";

export function ClampQcHubPage() {
  const meQuery = useIntegratorMe();
  const { session } = useAuth();
  const viewerUserId = session?.user?.id ?? null;
  const [laborOpenFlash, setLaborOpenFlash] = useState<string | null>(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(CLAMPQC_LABOR_OPEN_FLASH_STORAGE_KEY);
      if (raw != null && raw.trim() !== "") {
        sessionStorage.removeItem(CLAMPQC_LABOR_OPEN_FLASH_STORAGE_KEY);
        setLaborOpenFlash(raw.trim());
      }
    } catch {
      /* private mode or storage unavailable */
    }
  }, []);

  const workOrdersQuery = useQuery({
    queryKey: CLAMPQC_HUB_WORK_ORDERS_QUERY_KEY,
    queryFn: fetchClampQcHubWorkOrders,
    staleTime: 30_000,
  });

  const jobOperatorsQuery = useQuery({
    queryKey: clampQcActiveJobOperatorsQueryKey,
    queryFn: fetchClampQcActiveJobOperators,
    staleTime: 10_000,
    refetchInterval: CLAMP_QC_JOB_PRESENCE_INTERVAL_MS,
  });

  const laborListGateQuery = useQuery({
    queryKey: ["clamp-qc", "labor", "active-session", INSPECTION_LIST_LABOR_PAGE],
    queryFn: () => fetchLaborActiveSession({ page: INSPECTION_LIST_LABOR_PAGE, domain: "clampqc" }),
    enabled: Boolean(meQuery.data) && meQuery.isSuccess && hubModuleHasAnyAccess(meQuery.data, "clampqc"),
    staleTime: 10_000,
    refetchInterval: 25_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const otherActiveLaborPageKey = useMemo(() => {
    if (!laborListGateQuery.isSuccess || !laborListGateQuery.data) {
      return "";
    }
    const raw = (laborListGateQuery.data as Record<string, unknown>).active_on_other_page;
    return typeof raw === "string" ? raw.trim() : "";
  }, [laborListGateQuery.isSuccess, laborListGateQuery.data]);

  const openJobBlockedTooltipMessage = useMemo(() => {
    return otherActiveLaborPageKey !== ""
      ? `Labor is active on ${formatLaborLocationLabel(otherActiveLaborPageKey)}. Stop or close that session before opening another job here.`
      : "Checking whether labor is active on another page…";
  }, [otherActiveLaborPageKey]);

  const openJobBlockedForBundle = (bundle: ClampQcHubWorkOrderListItem): boolean => {
    if (!meQuery.isSuccess || !hubModuleHasAnyAccess(meQuery.data, "clampqc")) {
      return false;
    }
    return clampQcInspectionListShouldDisableOpenJobForSlug({
      laborGatePending: laborListGateQuery.isPending,
      activeSessionPayload:
        laborListGateQuery.isSuccess && laborListGateQuery.data
          ? (laborListGateQuery.data as Record<string, unknown>)
          : undefined,
      jobSlug: bundle.slug,
    });
  };

  const hubRows: ClampQcHubWorkOrderListItem[] = workOrdersQuery.data ?? [];

  const allLocations = useMemo(
    () => [...new Set(hubRows.map((row) => row.locationCode).filter(Boolean))].sort(),
    [hubRows],
  );
  const allSkus = useMemo(
    () => [...new Set(hubRows.map((row) => row.sku).filter(Boolean))].sort(),
    [hubRows],
  );

  const [locationFilter, setLocationFilter] = useState<Set<string>>(new Set());
  const locationsInitDone = useRef(false);

  useEffect(() => {
    if (hubRows.length === 0 || locationsInitDone.current) {
      return;
    }
    setLocationFilter(new Set(allLocations));
    locationsInitDone.current = true;
  }, [hubRows.length, allLocations]);

  const [skuFilter, setSkuFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<JobStatusFilter>("active");
  const [search, setSearch] = useState("");

  const toggleLocation = (code: string): void => {
    setLocationFilter((current) => {
      const next = new Set(current);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const filteredJobs = useMemo(() => {
    let rows = hubRows.filter((bundle) =>
      locationFilter.size === 0 ? true : locationFilter.has(bundle.locationCode),
    );
    if (skuFilter !== "all") {
      rows = rows.filter((bundle) => bundle.sku === skuFilter);
    }
    if (statusFilter !== "all") {
      rows = rows.filter((bundle) => bundle.status === statusFilter);
    }
    const needle = search.trim().toLowerCase();
    if (needle) {
      rows = rows.filter((bundle) => {
        const haystack = [
          bundle.workOrder,
          bundle.orderNumber ?? "",
          bundle.mvNumber ?? "",
          String(bundle.buildOrderId),
          bundle.title,
          bundle.sku,
          bundle.mfrReferenceNumber ?? "",
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(needle);
      });
    }
    return rows;
  }, [hubRows, locationFilter, skuFilter, statusFilter, search]);

  const loadError =
    workOrdersQuery.isError && workOrdersQuery.error instanceof Error ? workOrdersQuery.error.message : null;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-xl font-semibold text-slate-900">Inspection</h1>
        <p className="text-sm text-slate-600">
          Pick a clamp QC work order from <span className="font-medium">public.build_order</span> rows whose finished SKU
          ends with <span className="font-mono">-QC</span>. Open a job for the focused workspace session.
        </p>
      </header>

      {loadError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          Could not load work orders: {loadError}
        </p>
      ) : null}

      {laborOpenFlash ? (
        <div
          className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-900"
          role="status"
        >
          <p className="min-w-0 flex-1 leading-snug">{laborOpenFlash}</p>
          <Button size="sm" variant="secondary" type="button" onClick={() => setLaborOpenFlash(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Location</span>
            {workOrdersQuery.isLoading ? (
              <span className="text-xs text-slate-500">Loading…</span>
            ) : (
              allLocations.map((code) => {
                const isOn = locationFilter.has(code);
                return (
                  <Button
                    key={code}
                    size="sm"
                    variant={isOn ? "primary" : "secondary"}
                    type="button"
                    onClick={() => toggleLocation(code)}
                  >
                    {code}
                  </Button>
                );
              })
            )}
          </div>
          <p className="text-xs leading-snug text-slate-500">
            Only Smart Grid Solutions manufacturing sites appear here: the build order warehouse must have{" "}
            <span className="font-mono">public.location.mrp_site_code</span> <span className="font-mono">sgs</span>. The
            Location column is <span className="font-mono">public.location.name</span> (or an em dash if the name is
            blank).
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">SKU</span>
            <Button size="sm" variant={skuFilter === "all" ? "primary" : "secondary"} type="button" onClick={() => setSkuFilter("all")}>
              All
            </Button>
            {allSkus.map((sku) => (
              <Button
                key={sku}
                size="sm"
                variant={skuFilter === sku ? "primary" : "secondary"}
                type="button"
                onClick={() => setSkuFilter(sku)}
              >
                {sku}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Status</span>
            {(["all", "active", "on_hold", "complete"] satisfies JobStatusFilter[]).map((status) => (
              <Button
                key={status}
                size="sm"
                variant={statusFilter === status ? "primary" : "secondary"}
                type="button"
                onClick={() => setStatusFilter(status)}
              >
                {status === "all"
                  ? "All"
                  : status === "active"
                    ? "Active"
                    : status === "on_hold"
                      ? "On hold"
                      : "Complete"}
              </Button>
            ))}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600" htmlFor="clamp-qc-hub-search">
              Search
            </label>
            <Input
              id="clamp-qc-hub-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="GBO number, ERP number, SKU, manufacturer reference, build id…"
              className="mt-1 max-w-xl"
              autoComplete="off"
            />
          </div>
        </CardBody>
      </Card>

      <Card className="min-w-0">
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            {workOrdersQuery.isLoading ? (
              <>
                <Loader2 aria-hidden className="h-4 w-4 animate-spin text-slate-500" />
                <span>Loading clamp builds…</span>
              </>
            ) : (
              "Clamp QC work orders"
            )}
          </CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto overflow-y-hidden p-0 [-webkit-overflow-scrolling:touch]">
          {!workOrdersQuery.isLoading && hubRows.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-slate-600">
              No QC work orders returned. Expect rows where the finished SKU suffix is{" "}
              <span className="font-mono">-QC</span>.
            </p>
          ) : (
            <table className="min-w-[960px] w-full border-collapse text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2">Build</th>
                  <th className="px-3 py-2">SKU</th>
                  <th className="px-3 py-2 text-right tabular-nums">Quantity</th>
                  <th className="px-3 py-2 text-right tabular-nums">Complete</th>
                  <th className="px-3 py-2 text-right tabular-nums">Remaining</th>
                  <th className="px-3 py-2">Ship by</th>
                  <th className="px-3 py-2">Manufacturer reference</th>
                  <th className="px-3 py-2">Location</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredJobs.map((bundle) => {
                  const qty = bundle.quantity;
                  const complete = bundle.quantityComplete;
                  const remaining = Math.max(0, qty - complete);
                  return (
                    <tr key={bundle.slug} className="bg-white">
                      <td className="px-3 py-2">
                        <span className="font-semibold text-slate-900 tabular-nums">{bundle.workOrder}</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-800">{bundle.sku}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{qty.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{complete.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{remaining.toLocaleString()}</td>
                      <td className="px-3 py-2 tabular-nums text-slate-700">{bundle.mfrShipByDate}</td>
                      <td className="max-w-[140px] px-3 py-2 text-slate-600">{bundle.mfrReferenceNumber ?? "—"}</td>
                      <td className="px-3 py-2">
                        {bundle.locationId != null ? (
                          <ManufacturingLocationChip
                            locationId={bundle.locationId}
                            fallbackLabel={bundle.locationCode}
                          />
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <HoverHintContent
                          hoverHint={clampQcWorkOrderStatusHoverHintText({
                            hubStatus: bundle.hubStatus,
                            executionMode: bundle.executionMode,
                            mvStatus: bundle.mvStatus,
                          })}
                          align="center"
                        >
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ring-1",
                              clampQcWorkOrderStatusChipClassName(bundle.status, bundle.hubStatus),
                            )}
                          >
                            {clampQcWorkOrderStatusChipLabel(bundle.status, bundle.hubStatus)}
                          </span>
                        </HoverHintContent>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <PrePackCartonOperatorStack
                            operators={filterClampQcOperatorsForJobSlug(jobOperatorsQuery.data ?? [], bundle.slug)}
                            viewerUserId={viewerUserId}
                          />
                          {openJobBlockedForBundle(bundle) ? (
                          <ReadOnlyHoverLayer active layout="inline" hoverHint={openJobBlockedTooltipMessage}>
                            <span
                              className="cursor-not-allowed font-medium text-slate-400"
                              aria-disabled="true"
                              aria-label={openJobBlockedTooltipMessage}
                            >
                              Open job
                            </span>
                          </ReadOnlyHoverLayer>
                        ) : (
                          <Link
                            className="font-medium text-brand-700 hover:text-brand-800"
                            to={`/clamp-qc/jobs/${encodeURIComponent(bundle.slug)}`}
                          >
                            Open job
                          </Link>
                        )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!workOrdersQuery.isLoading && filteredJobs.length === 0 && hubRows.length > 0 ? (
            <p className="px-3 py-8 text-center text-sm text-slate-600">
              No builds match these filters. Turn on at least one location or widen SKU / status.
            </p>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
